import { type FC, type ReactNode, useId, useRef, useState } from "react";
import { type FieldError, get, useFormState } from "react-hook-form";
import { FileUploadController } from "react-hook-form-file-upload";
import { twMerge } from "tailwind-merge";

import { collaborationUploadFile } from "api/api-worksheets";
import { t } from "i18n/i18n";

type LabeledFileUploadControllerProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "children"
> & {
  wrapperClassName?: string;
  name: string;
  label: string;
  postLabel?: ReactNode;
  required?: boolean;
  validateFiles?: Parameters<typeof FileUploadController>[0]["validateFiles"];
  children:
    | ReactNode
    | ((props: {
        value: string | null;
        select: () => void;
        remove: () => void;
        isUploading: boolean;
      }) => ReactNode);
};

// TODO: make this work with FormField and unify all file upload components
export const LabeledFileUploadController: FC<LabeledFileUploadControllerProps> = ({
  id: idProp,
  className,
  wrapperClassName,
  name,
  label,
  postLabel,
  required = false,
  disabled = false,
  validateFiles,
  children,
  ...inputProps
}) => {
  const randomId = useId();
  const { errors } = useFormState({ name });
  const id = idProp || `${randomId}-${name}`;
  const errorId = `${id}-error`;

  const error = get(errors, name) as FieldError | undefined;
  const errorMessage = typeof error?.message === "string" ? error.message : undefined;
  const invalid = !!error;
  const showError = invalid && !disabled && !!errorMessage;

  const [isUploading, setIsUploading] = useState(false);
  const [isDragHover, setIsDragHover] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const labelNode = (
    <label
      className={twMerge(disabled ? "text-black-tint-70" : "text-black-tint-20", "body-s")}
      htmlFor={id}
    >
      {label}
    </label>
  );

  return (
    <div className={twMerge("flex flex-col", wrapperClassName)}>
      <div className="mb-1 flex items-center justify-between gap-2">
        {postLabel ? (
          <div className="flex items-center gap-2">
            {labelNode}
            {postLabel}
          </div>
        ) : (
          labelNode
        )}
        {!required && (
          <div
            className={twMerge(disabled ? "text-black-tint-70" : "text-black-tint-55", "body-xs")}
          >
            {t("common.optional")}
          </div>
        )}
      </div>
      <FileUploadController
        name={name}
        render={({ value, field, select, remove, getDropZoneProps }) => (
          <div
            {...getDropZoneProps({
              onDragEnter: () => setIsDragHover(true),
              onDragLeave: (event) => {
                // `dragleave` is also called when dragging over child elements of the dropzone,
                // so we can't simply call `setIsDragHover(false)` here -- otherwise, dragging
                // over a child would cancel the drag hover! Instead, we can check the mouse position
                // and see if we have actually left the dropzone before calling `setIsDragHover(false)`.
                const dropZoneEl = dropZoneRef.current;
                if (!dropZoneEl) return;
                const dropZoneRect = dropZoneEl.getBoundingClientRect();
                if (
                  event.clientY < dropZoneRect.top ||
                  event.clientY >= dropZoneRect.bottom ||
                  event.clientX < dropZoneRect.left ||
                  event.clientX >= dropZoneRect.right
                ) {
                  setIsDragHover(false);
                }
              },
              onDrop: () => setIsDragHover(false),
            })}
            className={twMerge("group", className)}
            data-disabled={disabled || undefined}
            data-drag-hover={isDragHover || undefined}
            ref={dropZoneRef}
          >
            <input
              {...field}
              aria-describedby={showError ? errorId : undefined}
              aria-invalid={invalid}
              className="hidden"
              disabled={disabled}
              id={id}
              {...inputProps}
            />
            {typeof children === "function"
              ? children({
                  value,
                  select,
                  remove,
                  isUploading,
                })
              : children}
          </div>
        )}
        rules={{ required: required ? t("error.field_required") : false }}
        uploadFiles={async (files) => {
          setIsUploading(true);
          try {
            const resp = await collaborationUploadFile(files[0]);
            return resp.url;
          } finally {
            setIsUploading(false);
          }
        }}
        validateFiles={validateFiles}
        droppable
      />
      {showError ? (
        <div className="body-xs mt-1 text-red" id={errorId}>
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
};
